import React from "react";
import { ChevronLeft } from "lucide-react";
import { useImageStudio, matchCondition } from "../../context/ImageStudioContext";
import { PresetField } from "../../service/apiService";
import ReferenceDropzone from "./ReferenceDropzone";

const inputBase =
  "w-full rounded-md bg-surface-2 border border-border-default px-3 py-2 text-sm text-primary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-violet-500/60";

const PresetForm: React.FC = () => {
  const {
    activePreset,
    closePreset,
    params,
    setParam,
    referencePreviewUrl,
    uploadingReference,
    uploadReference,
    clearReference,
    styleReferencePreviewUrl,
    uploadingStyleReference,
    uploadStyleReference,
    clearStyleReference,
  } = useImageStudio();

  if (!activePreset) return null;

  const visible = (f: PresetField) => !f.show_when || matchCondition(f.show_when, params);

  const addChip = (name: string, chip: string) => {
    const items = String(params[name] || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (items.includes(chip)) return;
    setParam(name, [...items, chip].join(", "));
  };

  const renderField = (f: PresetField) => {
    if (!visible(f)) return null;
    const value = params[f.name];

    switch (f.type) {
      case "image":
        return f.slot === "style" ? (
          <ReferenceDropzone
            key={f.name}
            label={f.label}
            previewUrl={styleReferencePreviewUrl}
            uploading={uploadingStyleReference}
            onDrop={uploadStyleReference}
            onClear={clearStyleReference}
          />
        ) : (
          <ReferenceDropzone
            key={f.name}
            label={f.label}
            previewUrl={referencePreviewUrl}
            uploading={uploadingReference}
            onDrop={uploadReference}
            onClear={clearReference}
          />
        );

      case "segmented":
        return (
          <div key={f.name} className="space-y-1">
            <div className="flex gap-0.5 rounded-lg bg-surface-2 p-1">
              {(f.options || []).map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setParam(f.name, o.value)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                    value === o.value
                      ? "bg-surface-overlay text-primary shadow-sm"
                      : "text-muted hover:text-primary"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {(() => {
              const opt = (f.options || []).find((o) => o.value === value);
              return opt?.help ? (
                <p className="text-[11px] leading-snug text-subtle">{opt.help}</p>
              ) : null;
            })()}
          </div>
        );

      case "select":
        return (
          <div key={f.name} className="space-y-1">
            <label className="block text-xs font-medium text-muted">
              {f.label}
              {f.required && <span className="text-violet-500"> *</span>}
            </label>
            <select
              value={value ?? ""}
              onChange={(e) => setParam(f.name, e.target.value)}
              className={inputBase}
            >
              {(f.required || value == null) && (
                <option value="" disabled>
                  Select…
                </option>
              )}
              {(f.options || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {f.help && <p className="text-[11px] text-subtle">{f.help}</p>}
          </div>
        );

      case "chips":
        return (
          <div key={f.name} className="space-y-1.5">
            <label className="block text-xs font-medium text-muted">{f.label}</label>
            <textarea
              value={value ?? ""}
              onChange={(e) => setParam(f.name, e.target.value)}
              rows={2}
              placeholder="e.g. sectional sofa, area rug"
              className={`${inputBase} resize-none`}
            />
            <div className="flex flex-wrap gap-1">
              {(f.suggestions || []).map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => addChip(f.name, chip)}
                  className="rounded-full border border-border-default bg-surface-2 px-2 py-0.5 text-[11px] text-muted hover:bg-surface-3 hover:text-primary"
                >
                  + {chip}
                </button>
              ))}
            </div>
          </div>
        );

      case "cards":
        return (
          <div key={f.name} className="space-y-1.5">
            <label className="block text-xs font-medium text-muted">
              {f.label}
              {f.required && <span className="text-violet-500"> *</span>}
            </label>
            <div className="space-y-1.5">
              {(f.options || []).map((o) => {
                const selected = value === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setParam(f.name, o.value)}
                    className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
                      selected
                        ? "border-violet-500 bg-surface-brand"
                        : "border-border-default bg-surface-2 hover:bg-surface-3"
                    }`}
                  >
                    <div className="text-sm font-medium text-primary">{o.label}</div>
                    {o.summary && (
                      <div className="mt-0.5 text-[11px] leading-snug text-subtle">{o.summary}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "slider": {
        const min = f.min ?? 0;
        const max = f.max ?? 1;
        const step = f.step ?? 0.05;
        const v = value ?? f.default ?? min;
        return (
          <div key={f.name} className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted">{f.label}</label>
              <span className="text-xs tabular-nums text-subtle">
                {f.as === "percent" ? `${Math.round(v * 100)}%` : v}
              </span>
            </div>
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={v}
              onChange={(e) => setParam(f.name, parseFloat(e.target.value))}
              className="w-full accent-violet-500"
            />
            {f.help && <p className="text-[11px] text-subtle">{f.help}</p>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={closePreset}
        className="flex items-center gap-1 text-xs font-medium text-muted hover:text-primary"
      >
        <ChevronLeft size={14} /> All presets
      </button>
      <div>
        <h2 className="text-sm font-semibold text-primary">{activePreset.label}</h2>
        <p className="mt-0.5 text-[11px] leading-snug text-subtle">{activePreset.description}</p>
      </div>
      {activePreset.fields.map(renderField)}
    </div>
  );
};

export default PresetForm;
